import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import * as Api from '../../../extViewmodels';
import { CsmMeetingTypes, IAccount, IUser } from '../../../extViewmodels';
import { useDebounceCallback } from '../../../hooks/useDebounceValue';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import { useAdminAccountFilterQuery } from '../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import {
	useAdminAccountMeetingNoteMutation,
	useAdminCalendarMeetingTranscriptsMutation,
	useAdminTranscriptSummarizeMutation,
} from '../../../queries/Admin/Calendar';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import * as AdminApi from '../../../viewmodels/AdminViewModels';
import {
	ICalendarMeetingInfo,
	ICalendarMeetingsResponse,
	ICalendarMeetingWithTranscripts,
	ITranscriptMatch,
} from '../../../viewmodels/CalendarViewModels';
import { RealMagicAccountIds } from '../../../extViewmodels/viewModels/index';
import { Button } from '../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxClear,
	ComboboxContent,
	ComboboxControl,
	ComboboxEmpty,
	ComboboxIcon,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
	ComboboxValue,
} from '../../../web/components-v2/Combobox';
import { ComboboxAvatarItem } from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { Dialog, DialogBody, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { DayPicker } from '../../../web/components/DayPicker';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components/Popover';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components/Select';
import { bs } from '../../../web/styles/styles';
import { styleSheet } from './styles';

const DEFAULT_MEETING_DURATION_MINUTES = 60;
const DEFAULT_NO_SHOW_DURATION_MINUTES = 30;

export function LogMeetingNotes() {
	const id = React.useId();
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('LogMeetingNotes');

	// State for user selection
	const [selectedUser, setSelectedUser] = React.useState<IUser>(userSession.user);
	const [userSearchValue, setUserSearchValue] = React.useState(getDisplayName(userSession.user));
	const {
		users,
		isLoading: isLoadingUsers,
		loadAllUsers,
		error: usersError,
	} = useLoadAllUsers({
		pageSize: 500,
	});
	const activeUsers = React.useMemo(() => {
		if (!users.length) return [userSession.user];

		return users
			.filter((user: IUser) => user.activationStatus === 'ACTIVE')
			.sort((a: IUser, b: IUser) => getDisplayName(a).localeCompare(getDisplayName(b)));
	}, [users, userSession]);
	React.useEffect(() => {
		loadAllUsers();
	}, [loadAllUsers]);

	// Account search with debounce
	const [debouncedAccountSearch, setDebouncedAccountSearch] = React.useState('');
	const debouncedAccountSearchCallback = useDebounceCallback((value: string) => {
		setDebouncedAccountSearch(value);
	}, 300);
	const handleAccountSearchValueChange = React.useCallback(
		(value: string) => {
			setAccountSearchValue(value);
			debouncedAccountSearchCallback(value);
		},
		[debouncedAccountSearchCallback]
	);

	// Account filter for search
	const accountFilterRequest = React.useMemo(() => {
		const criteria: AdminApi.IAccountFilterCriteria[] = [];

		// Add name filter when searching, just like in /accounts
		if (debouncedAccountSearch && debouncedAccountSearch.length > 0) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Name,
				value: debouncedAccountSearch,
			});
		}

		return {
			criteria,
			op: Api.FilterOperator.And,
		} as AdminApi.IAccountsFilterRequest;
	}, [debouncedAccountSearch]);

	const accountsQuery = useAdminAccountFilterQuery({
		filter: accountFilterRequest,
		autocompleteFragment: debouncedAccountSearch || undefined,
		pageSize: 50,
	});

	const accounts = React.useMemo(() => {
		if (!accountsQuery.data) return [];
		return accountsQuery.data.pages.flatMap(page => page.values || []);
	}, [accountsQuery.data]);

	// State for date - default to today
	const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
	const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);

	// State for meetings data
	const [meetingsData, setMeetingsData] = React.useState<ICalendarMeetingsResponse | null>(null);
	const [isLoadingMeetings, setIsLoadingMeetings] = React.useState<boolean>(false);
	const [meetingsError, setMeetingsError] = React.useState<string | null>(null);

	// State for meetings display
	const [selectedMeeting, setSelectedMeeting] = React.useState<ICalendarMeetingInfo | null>(null);
	const [selectedMeetingWithTranscripts, setSelectedMeetingWithTranscripts] =
		React.useState<ICalendarMeetingWithTranscripts | null>(null);
	const [selectedTranscript, setSelectedTranscript] = React.useState<ITranscriptMatch | null>(null);
	const [isTranscriptDialogOpen, setIsTranscriptDialogOpen] = React.useState(false);
	const [expandedTranscripts, setExpandedTranscripts] = React.useState<Set<number>>(new Set());

	// State for meeting note form
	const [showMeetingNoteForm, setShowMeetingNoteForm] = React.useState(false);
	const [selectedAccount, setSelectedAccount] = React.useState<IAccount | null>(null);
	const [accountSearchValue, setAccountSearchValue] = React.useState('');
	const [accountIdToFetch, setAccountIdToFetch] = React.useState<string | null>(null);

	// Query to fetch full account data when resolving from attendees
	const fullAccountQuery = useAdminAccountQuery({
		accountId: accountIdToFetch || '',
		enabled: !!accountIdToFetch,
	});

	// Set selected account when full account data is fetched
	React.useEffect(() => {
		if (fullAccountQuery.data) {
			setSelectedAccount(fullAccountQuery.data);
			setAccountSearchValue(fullAccountQuery.data.companyName || '');
			setAccountIdToFetch(null); // Reset to prevent re-fetching
		}
	}, [fullAccountQuery.data]);
	const [meetingNoteContent, setMeetingNoteContent] = React.useState('');
	const [meetingType, setMeetingType] = React.useState<CsmMeetingTypes | null>(null);
	const [meetingStatus, setMeetingStatus] = React.useState('Completed');
	const [duration, setDuration] = React.useState<number>(DEFAULT_MEETING_DURATION_MINUTES);
	const [isOnboarding, setIsOnboarding] = React.useState(false);
	const [nextMeetingDate, setNextMeetingDate] = React.useState<Date | null>(null);
	const [showNextMeetingDatePicker, setShowNextMeetingDatePicker] = React.useState(false);
	const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
	const [isSavingNote, setIsSavingNote] = React.useState(false);

	// Track which meetings have had notes recorded (in page state only)
	const [meetingsWithNotesRecorded, setMeetingsWithNotesRecorded] = React.useState<Set<string>>(new Set());
	const [meetingsMarkedNoShow, setMeetingsMarkedNoShow] = React.useState<Set<string>>(new Set());

	// State for no show dialog
	const [isNoShowDialogOpen, setIsNoShowDialogOpen] = React.useState(false);
	const [noShowMeeting, setNoShowMeeting] = React.useState<ICalendarMeetingInfo | null>(null);
	const [noShowAccount, setNoShowAccount] = React.useState<IAccount | null>(null);
	const [noShowAccountSearchValue, setNoShowAccountSearchValue] = React.useState('');
	const [isSavingNoShow, setIsSavingNoShow] = React.useState(false);

	// State for web team confirmation
	const [showWebTeamConfirmation, setShowWebTeamConfirmation] = React.useState(false);
	const [webTeamSummary, setWebTeamSummary] = React.useState<string | null>(null);
	const [isGeneratingWebTeamSummary, setIsGeneratingWebTeamSummary] = React.useState(false);

	// Web Team editable Deep Research fields
	const [webTeamBusinessType, setWebTeamBusinessType] = React.useState<string>('');
	const [webTeamLocation, setWebTeamLocation] = React.useState<string>('');
	const [webTeamWebsiteUrl, setWebTeamWebsiteUrl] = React.useState<string>('');

	// State for content team confirmation
	const [showContentTeamConfirmation, setShowContentTeamConfirmation] = React.useState(false);
	const [contentTeamSummary, setContentTeamSummary] = React.useState<string | null>(null);
	const [isGeneratingContentTeamSummary, setIsGeneratingContentTeamSummary] = React.useState(false);

	// Mutations
	const transcriptsMutation = useAdminCalendarMeetingTranscriptsMutation();
	const summarizeMutation = useAdminTranscriptSummarizeMutation();
	const saveMeetingNoteMutation = useAdminAccountMeetingNoteMutation();

	// Handle user selection
	const handleUserSelected = React.useCallback((user: IUser) => {
		if (user) {
			setSelectedUser(user);
			setSelectedMeeting(null);
			setSelectedMeetingWithTranscripts(null);
			setSelectedTranscript(null);
			setMeetingsData(null);
			setMeetingsError(null);
		}
	}, []);

	// Date navigation helpers
	const isToday = React.useMemo(() => {
		const today = new Date();
		return (
			selectedDate.getFullYear() === today.getFullYear() &&
			selectedDate.getMonth() === today.getMonth() &&
			selectedDate.getDate() === today.getDate()
		);
	}, [selectedDate]);

	const goToPreviousDay = React.useCallback(() => {
		const newDate = new Date(selectedDate);
		newDate.setDate(newDate.getDate() - 1);
		setSelectedDate(newDate);
	}, [selectedDate]);

	const goToNextDay = React.useCallback(() => {
		if (!isToday) {
			const newDate = new Date(selectedDate);
			newDate.setDate(newDate.getDate() + 1);
			setSelectedDate(newDate);
		}
	}, [selectedDate, isToday]);

	const handleDatePickerSelect = React.useCallback((day: Date) => {
		setSelectedDate(day);
		setIsDatePickerOpen(false);
	}, []);

	const formatDisplayDate = (date: Date) => {
		return date.toLocaleDateString('en-US', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	// Fetch meetings for the selected date
	const fetchMeetings = React.useCallback(async () => {
		if (!selectedUser) {
			return;
		}

		// Reset state
		setSelectedMeeting(null);
		setSelectedMeetingWithTranscripts(null);
		setSelectedTranscript(null);
		setMeetingsError(null);

		setIsLoadingMeetings(true);

		try {
			// Convert to UTC for start and end of the selected day
			const startDateUtc = new Date(
				Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0)
			);
			const endDateUtc = new Date(
				Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59)
			);

			const url = `admin/calendar/meetings?userId=${encodeURIComponent(selectedUser.id)}&accountId=${encodeURIComponent(userSession.account.id)}&startDate=${startDateUtc.toISOString()}&endDate=${endDateUtc.toISOString()}`;

			const result = await userSession.webServiceHelper.callAsync<ICalendarMeetingsResponse>(url, 'GET');
			setMeetingsData(result);
		} catch (error) {
			logApiError('FetchMeetings-Error', error);
			setMeetingsError('Error loading meetings. Please try again or contact support.');
		} finally {
			setIsLoadingMeetings(false);
		}
	}, [selectedUser, selectedDate, userSession, logApiError]);

	// Auto-fetch when user or date changes
	React.useEffect(() => {
		if (selectedUser) {
			fetchMeetings();
		}
	}, [selectedUser, selectedDate, fetchMeetings]);

	// Handle find transcript
	const handleFindTranscript = React.useCallback(
		async (meeting: ICalendarMeetingInfo) => {
			if (!selectedUser || !meeting.startTime) {
				return;
			}

			setSelectedMeeting(meeting);
			setSelectedMeetingWithTranscripts(null);
			setSelectedTranscript(null);
			setExpandedTranscripts(new Set());
			setShowMeetingNoteForm(false);
			setIsTranscriptDialogOpen(true);

			try {
				const meetingDate = new Date(meeting.startTime);
				const startOfDay = new Date(meetingDate);
				startOfDay.setHours(0, 0, 0, 0);
				const endOfDay = new Date(meetingDate);
				endOfDay.setHours(23, 59, 59, 999);

				const result = await transcriptsMutation.mutateAsync({
					userId: selectedUser.id,
					accountId: userSession.account.id,
					userEmail: selectedUser.email,
					startDate: startOfDay,
					endDate: endOfDay,
				});

				// Find the matching meeting in the response
				const matchedMeeting = result.meetings?.find(
					m => m.meeting?.id === meeting.id || m.meeting?.iCalUid === meeting.iCalUid
				);

				setSelectedMeetingWithTranscripts(matchedMeeting || null);
			} catch (error) {
				logApiError('FindTranscript-Error', error);
				errorMessages?.pushApiError(error);
			}
		},
		[selectedUser, userSession.account.id, transcriptsMutation, logApiError, errorMessages]
	);

	// Toggle transcript expansion
	const toggleTranscriptExpansion = React.useCallback((index: number) => {
		setExpandedTranscripts(prev => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	}, []);

	// Resolve account from meeting attendees
	const resolveAccountFromAttendees = React.useCallback(() => {
		const attendees = selectedMeetingWithTranscripts?.meeting?.attendees || [];
		// Look for an attendee with an accountId and companyName, excluding the organizer and Real Magic accounts
		const attendeeWithAccount = attendees.find(
			a => !a.isOrganizer && a.accountId && a.companyName && !RealMagicAccountIds.has(a.accountId)
		);
		if (attendeeWithAccount && attendeeWithAccount.accountId) {
			// Trigger fetch of full account data with features
			setAccountIdToFetch(attendeeWithAccount.accountId);
		}
	}, [selectedMeetingWithTranscripts]);

	// Handle record meeting note (replaces generate summary)
	const handleRecordMeetingNote = React.useCallback(
		async (transcript: ITranscriptMatch) => {
			if (!transcript.transcriptContent) {
				return;
			}

			setSelectedTranscript(transcript);

			try {
				const attendeeNames =
					selectedMeetingWithTranscripts?.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown') ||
					[];

				// Generate the summary
				const summaryResult = await summarizeMutation.mutateAsync({
					transcriptContent: transcript.transcriptContent,
					attendeeNames,
					meetingType: 'SuccessClientMeeting',
					industry: selectedAccount?.additionalInfo?.industry,
				});

				// Pre-populate form fields
				setMeetingNoteContent(summaryResult?.summary || '');
				setDuration(selectedMeeting?.durationMinutes || transcript.durationMinutes || 60);
				setMeetingStatus('Completed');
				setValidationErrors([]);

				// Resolve account from attendees
				resolveAccountFromAttendees();

				// Show the form
				setShowMeetingNoteForm(true);
			} catch (error) {
				logApiError('RecordMeetingNote-Error', error);
				errorMessages?.pushApiError(error);
			}
		},
		[
			selectedMeetingWithTranscripts,
			selectedMeeting,
			summarizeMutation,
			logApiError,
			errorMessages,
			resolveAccountFromAttendees,
			selectedAccount?.additionalInfo?.industry,
		]
	);

	// Validate form before saving
	const validateForm = React.useCallback(() => {
		const errors: string[] = [];

		if (!selectedAccount) {
			errors.push('Please select an account.');
		}

		if (!meetingNoteContent || meetingNoteContent.trim() === '') {
			errors.push('Please enter meeting notes.');
		}

		if (!meetingType) {
			errors.push('Please select a meeting type.');
		}

		setValidationErrors(errors);
		return errors.length === 0;
	}, [selectedAccount, meetingNoteContent, meetingType]);

	// Handle save meeting note
	const handleSaveMeetingNote = React.useCallback(async () => {
		// Validate form
		if (!validateForm()) {
			return;
		}

		setIsSavingNote(true);

		try {
			// Prepare meeting tags
			const meetingTags: string[] = [];
			if (isOnboarding) {
				meetingTags.push('Onboarding');
			}

			// Save the meeting note
			await saveMeetingNoteMutation.mutateAsync({
				account: selectedAccount,
				meetingType,
				meetingStatus,
				durationInMinutes: duration,
				meetingTags,
				noteContent: meetingNoteContent,
			});

			// Show success message
			toast.success('Meeting note saved successfully!');

			// Update next meeting date on account if set
			if (nextMeetingDate && selectedAccount?.id) {
				try {
					await userSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${selectedAccount.id}/additionalInfo/nextMeeting?nextMeeting=${encodeURIComponent(moment(nextMeetingDate).format('MM/DD/YYYY'))}`,
						'PUT'
					);
				} catch (error) {
					// Log error but don't fail the whole operation since meeting note was saved
					logApiError('UpdateNextMeetingDate-Error', error);
					toast.error('Meeting note saved, but failed to update next meeting date');
				}
			}

			// Track that this meeting has notes recorded
			if (selectedMeeting?.id) {
				setMeetingsWithNotesRecorded(prev => new Set(prev).add(selectedMeeting.id));
			}

			// Check for enabled features
			const isWebsiteAccount = selectedAccount?.features?.websites?.enabled === true;
			const isContentCalendarAccount = selectedAccount?.features?.customContentCalendar?.enabled === true;

			if (isWebsiteAccount || isContentCalendarAccount) {
				// Show confirmation screen(s) instead of closing
				setShowMeetingNoteForm(false);

				if (isWebsiteAccount) {
					setShowWebTeamConfirmation(true);

					// Pre-populate editable Deep Research fields
					setWebTeamBusinessType(selectedAccount?.additionalInfo?.industry || '');

					const address = selectedAccount?.planDetails?.address;
					const location = address?.city && address?.stateProvince ? `${address.city}, ${address.stateProvince}` : '';
					setWebTeamLocation(location);

					// Use website URL if available, otherwise fall back to email domain
					const websiteUrl = selectedAccount?.features?.websites?.websiteUrl;
					const emailDomain = selectedAccount?.emailDomain;
					const emailDomainUrl = emailDomain && emailDomain !== '@none.com' ? `https://${emailDomain}` : '';
					setWebTeamWebsiteUrl(websiteUrl || emailDomainUrl);
				}

				if (isContentCalendarAccount) {
					setShowContentTeamConfirmation(true);
				}
				// Keep dialog open, don't reset state yet
			} else {
				// Close the form and reset state
				setShowMeetingNoteForm(false);
				setIsTranscriptDialogOpen(false);
				setSelectedAccount(null);
				setAccountSearchValue('');
				setAccountIdToFetch(null);
				setMeetingNoteContent('');
				setMeetingType(null);
				setMeetingStatus('Completed');
				setDuration(DEFAULT_MEETING_DURATION_MINUTES);
				setIsOnboarding(false);
				setNextMeetingDate(null);
				setShowNextMeetingDatePicker(false);
				setValidationErrors([]);
			}
		} catch (error) {
			logApiError('SaveMeetingNote-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingNote(false);
		}
	}, [
		validateForm,
		selectedAccount,
		meetingType,
		meetingStatus,
		duration,
		isOnboarding,
		meetingNoteContent,
		saveMeetingNoteMutation,
		logApiError,
		errorMessages,
		selectedMeeting,
		nextMeetingDate,
		userSession,
	]);

	// Handle generating web team summary
	const handleGenerateWebTeamSummary = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent) {
			return;
		}

		setIsGeneratingWebTeamSummary(true);

		try {
			const attendeeNames =
				selectedMeetingWithTranscripts?.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown') ||
				[];

			// Generate the web team summary with Deep Research fields
			const summaryResult = await summarizeMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				attendeeNames,
				meetingType: 'SuccessClientMeetingForWebTeam',
				industry: selectedAccount?.additionalInfo?.industry,
				// Deep Research fields - use editable state values
				deepResearchAccountId: selectedAccount?.id,
				deepResearchBusinessName: selectedAccount?.companyName,
				deepResearchBusinessType: webTeamBusinessType || undefined,
				deepResearchLocation: webTeamLocation || undefined,
				deepResearchWebsiteUrl: webTeamWebsiteUrl || undefined,
			});

			setWebTeamSummary(summaryResult?.summary || '');
		} catch (error) {
			logApiError('GenerateWebTeamSummary-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingWebTeamSummary(false);
		}
	}, [
		selectedTranscript,
		selectedMeetingWithTranscripts,
		selectedAccount,
		summarizeMutation,
		webTeamBusinessType,
		webTeamLocation,
		webTeamWebsiteUrl,
		logApiError,
		errorMessages,
	]);

	// Handle skipping web team summary
	const handleSkipWebTeamSummary = React.useCallback(() => {
		setShowWebTeamConfirmation(false);
		setWebTeamSummary(null);
		setWebTeamBusinessType('');
		setWebTeamLocation('');
		setWebTeamWebsiteUrl('');
	}, []);

	// Handle copying web team summary to clipboard
	const handleCopyWebTeamSummary = React.useCallback(() => {
		if (webTeamSummary) {
			navigator.clipboard.writeText(webTeamSummary);
			toast.success('Summary copied to clipboard!');
		}
	}, [webTeamSummary]);

	// Handle generating content team summary
	const handleGenerateContentTeamSummary = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent) {
			return;
		}

		setIsGeneratingContentTeamSummary(true);

		try {
			const attendeeNames =
				selectedMeetingWithTranscripts?.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown') ||
				[];

			const summaryResult = await summarizeMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				attendeeNames,
				meetingType: 'SuccessClientMeetingForContentTeam',
				industry: selectedAccount?.additionalInfo?.industry,
				// For saving notes on account
				deepResearchAccountId: selectedAccount?.id,
				deepResearchBusinessName: selectedAccount?.companyName,
			});

			setContentTeamSummary(summaryResult?.summary || '');
		} catch (error) {
			logApiError('GenerateContentTeamSummary-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingContentTeamSummary(false);
		}
	}, [
		selectedTranscript,
		selectedMeetingWithTranscripts,
		selectedAccount,
		summarizeMutation,
		logApiError,
		errorMessages,
	]);

	// Handle skipping content team summary
	const handleSkipContentTeamSummary = React.useCallback(() => {
		setShowContentTeamConfirmation(false);
		setContentTeamSummary(null);
	}, []);

	// Handle copying content team summary to clipboard
	const handleCopyContentTeamSummary = React.useCallback(() => {
		if (contentTeamSummary) {
			navigator.clipboard.writeText(contentTeamSummary);
			toast.success('Summary copied to clipboard!');
		}
	}, [contentTeamSummary]);

	// Handle closing all confirmation screens
	const handleCloseConfirmationScreen = React.useCallback(() => {
		setShowWebTeamConfirmation(false);
		setShowContentTeamConfirmation(false);
		setWebTeamSummary(null);
		setContentTeamSummary(null);
		setWebTeamBusinessType('');
		setWebTeamLocation('');
		setWebTeamWebsiteUrl('');
		setIsTranscriptDialogOpen(false);
		setSelectedAccount(null);
		setAccountSearchValue('');
		setAccountIdToFetch(null);
		setMeetingNoteContent('');
		setMeetingType(null);
		setMeetingStatus('Completed');
		setDuration(DEFAULT_MEETING_DURATION_MINUTES);
		setIsOnboarding(false);
		setNextMeetingDate(null);
		setShowNextMeetingDatePicker(false);
		setValidationErrors([]);
	}, []);

	// Handle opening the no show dialog
	const handleMarkNoShow = React.useCallback((meeting: ICalendarMeetingInfo) => {
		setNoShowMeeting(meeting);

		// Try to resolve account from attendees (excluding organizer)
		const attendees = meeting?.attendees || [];
		const attendeeWithAccount = attendees.find(a => !a.isOrganizer && a.accountId && a.companyName);

		if (attendeeWithAccount) {
			const account: IAccount = {
				id: attendeeWithAccount.accountId,
				companyName: attendeeWithAccount.companyName,
			};
			setNoShowAccount(account);
			setNoShowAccountSearchValue(attendeeWithAccount.companyName);
		} else {
			setNoShowAccount(null);
			setNoShowAccountSearchValue('');
		}

		setIsNoShowDialogOpen(true);
	}, []);

	// Handle no show account search value change
	const handleNoShowAccountSearchValueChange = React.useCallback(
		(value: string) => {
			setNoShowAccountSearchValue(value);
			debouncedAccountSearchCallback(value);
		},
		[debouncedAccountSearchCallback]
	);

	// Handle saving the no show
	const handleSaveNoShow = React.useCallback(async () => {
		if (!noShowAccount || !noShowMeeting) {
			toast.error('Please select an account');
			return;
		}

		setIsSavingNoShow(true);

		try {
			// Save the no show with minimal data
			await saveMeetingNoteMutation.mutateAsync({
				account: noShowAccount,
				meetingType: CsmMeetingTypes.CheckIn, // Default to Check-in
				meetingStatus: 'No showed',
				durationInMinutes: noShowMeeting.durationMinutes || DEFAULT_NO_SHOW_DURATION_MINUTES,
				meetingTags: [],
				noteContent: 'No show',
			});

			// Show success message
			toast.success('No show recorded successfully!');

			// Track that this meeting has been marked as no show
			if (noShowMeeting.id) {
				setMeetingsMarkedNoShow(prev => new Set(prev).add(noShowMeeting.id));
			}

			// Close the dialog and reset state
			setIsNoShowDialogOpen(false);
			setNoShowMeeting(null);
			setNoShowAccount(null);
			setNoShowAccountSearchValue('');
		} catch (error) {
			logApiError('SaveNoShow-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingNoShow(false);
		}
	}, [noShowAccount, noShowMeeting, saveMeetingNoteMutation, logApiError, errorMessages]);

	// Format time for display in Eastern time (date is already shown in header)
	// API returns UTC times, so we need to treat them as UTC and convert to ET
	const formatTime = (dateString: string) => {
		if (!dateString) return 'N/A';
		// Ensure the date string is treated as UTC by appending 'Z' if not present
		const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
		const date = new Date(utcDateString);
		return date.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'America/New_York',
		});
	};

	// Get match score style
	const getMatchScoreStyle = (score: number) => {
		if (score >= 70) return styleSheet.matchScoreHigh;
		if (score >= 50) return styleSheet.matchScoreMedium;
		return styleSheet.matchScoreLow;
	};

	// User search status
	let userSearchStatus: React.ReactNode = '';
	if (isLoadingUsers) {
		userSearchStatus = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Searching Users...
			</div>
		);
	} else if (usersError) {
		userSearchStatus = 'Error loading users';
	} else if (activeUsers.length === 0 && userSearchValue) {
		userSearchStatus = `No users found for "${userSearchValue}"`;
	} else if (activeUsers.length > 0) {
		userSearchStatus = `${activeUsers.length} User${activeUsers.length === 1 ? '' : 's'} found`;
	} else if (!userSearchValue) {
		userSearchStatus = 'Start typing to search users...';
	}
	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.section)}>
				<h2 className={css(styleSheet.sectionTitle)}>Log Meeting Notes</h2>
				<p>View calendar meetings for a user and match them with Zoom transcripts to generate AI summaries.</p>
			</div>

			{/* User Selection */}
			<div className={css(styleSheet.section)}>
				<div className={css(styleSheet.formGroup)}>
					<label htmlFor={`${id}-user-combobox`} className={css(styleSheet.label)}>
						Select User
					</label>
					<Combobox
						items={activeUsers}
						value={selectedUser}
						onValueChange={handleUserSelected}
						inputValue={userSearchValue}
						onInputValueChange={setUserSearchValue}
						itemToStringLabel={(user: IUser) => getDisplayName(user)}
						isItemEqualToValue={(item: IUser, value: IUser) => item.id === value.id}
					>
						<ComboboxControl>
							<ComboboxValue>
								<ComboboxInput placeholder='Search user...' id={`${id}-user-combobox`} />
							</ComboboxValue>
						</ComboboxControl>
						<ComboboxContent>
							<ComboboxStatus>{userSearchStatus}</ComboboxStatus>
							<ComboboxList>
								{(user: IUser) => (
									<ComboboxAvatarItem
										key={user.id}
										value={user}
										avatarImageUrl={user.profilePic}
										avatarFallbackText={user.firstName?.substring(0, 1) + user.lastName?.substring(0, 1)}
										title={getDisplayName(user)}
										description={user.email}
									/>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
					{selectedUser && (
						<div className={css(styleSheet.userInfo)}>
							✓ Selected: {getDisplayName(selectedUser)} ({selectedUser.email})
						</div>
					)}
				</div>

				{/* Date Navigation */}
				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.label)}>Select Date</label>
					<div className={css(styleSheet.dateNavigationContainer)}>
						<button
							className={css(styleSheet.dateNavButton)}
							onClick={goToPreviousDay}
							disabled={isLoadingMeetings}
							aria-label='Previous day'
						>
							←
						</button>
						<Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
							<PopoverTrigger asChild>
								<button
									className={css(styleSheet.dateDisplay, styleSheet.dateDisplayClickable)}
									disabled={isLoadingMeetings}
									aria-label='Select date'
								>
									{formatDisplayDate(selectedDate)}
								</button>
							</PopoverTrigger>
							<PopoverContent side='bottom' align='center'>
								<DayPicker
									selectedDays={selectedDate}
									onDayClick={handleDatePickerSelect}
									maxDate={new Date()}
									allowPastDates
								/>
							</PopoverContent>
						</Popover>
						<button
							className={css(styleSheet.dateNavButton)}
							onClick={goToNextDay}
							disabled={isToday || isLoadingMeetings}
							aria-label='Next day'
						>
							→
						</button>
					</div>
					{isToday && <div className={css(styleSheet.todayIndicator)}>Today</div>}
				</div>
			</div>

			{/* Error Display */}
			{meetingsError && <div className={css(styleSheet.errorMessage)}>{meetingsError}</div>}

			{/* Loading State */}
			{isLoadingMeetings && (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
				</div>
			)}

			{/* Meetings Table */}
			{meetingsData && !isLoadingMeetings && (
				<div className={css(styleSheet.section)}>
					<h3 className={css(styleSheet.sectionTitle)}>
						Meetings for {formatDisplayDate(selectedDate)} ({meetingsData.filteredCount})
					</h3>
					{meetingsData.meetings && meetingsData.meetings.length > 0 ? (
						<table className={css(styleSheet.meetingsTable)}>
							<thead className={css(styleSheet.tableHeader)}>
								<tr>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.meetingDetailsHeader)}>Meeting Details</th>
									<th className={css(styleSheet.tableHeaderCell)}>Time</th>
									<th className={css(styleSheet.tableHeaderCell)}>Duration</th>
									<th className={css(styleSheet.tableHeaderCell)}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{meetingsData.meetings.map((meeting, index) => {
									const nonOrganizerAttendees = meeting.attendees?.filter(a => !a.isOrganizer) || [];
									return (
										<tr key={meeting.id || index} className={css(styleSheet.tableRow)}>
											<td className={css(styleSheet.tableCell, styleSheet.meetingDetailsCell)}>
												<div className={css(styleSheet.meetingTitle)}>{meeting.summary || 'Untitled Meeting'}</div>
												{nonOrganizerAttendees.length > 0 && (
													<div className={css(styleSheet.attendeesPreview)}>
														<strong>Attendees: </strong>
														{nonOrganizerAttendees.map(a => a.userName || a.name || a.email || 'Unknown').join(', ')}
														{!meetingsMarkedNoShow.has(meeting.id) && (
															<button
																className={css(styleSheet.markNoShowButton)}
																onClick={e => {
																	e.stopPropagation();
																	handleMarkNoShow(meeting);
																}}
															>
																Mark No Show
															</button>
														)}
													</div>
												)}
											</td>
											<td className={css(styleSheet.tableCell)}>{formatTime(meeting.startTime)}</td>
											<td className={css(styleSheet.tableCell)}>{meeting.durationMinutes} min</td>
											<td className={css(styleSheet.tableCell)}>
												{meetingsMarkedNoShow.has(meeting.id) ? (
													<span className={css(styleSheet.noShowIndicator)}>No Show</span>
												) : meetingsWithNotesRecorded.has(meeting.id) ? (
													<span className={css(styleSheet.notesRecordedIndicator)}>Notes Recorded</span>
												) : (
													<button
														className={css(styleSheet.actionButton)}
														onClick={() => handleFindTranscript(meeting)}
													>
														Find Transcript
													</button>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					) : (
						<div className={css(styleSheet.noDataMessage)}>
							No meetings found in the specified date range. Private meetings, all-day events, meetings over 3 hours,
							and solo meetings are filtered out.
						</div>
					)}
				</div>
			)}

			{/* Transcript Dialog */}
			<Dialog
				open={isTranscriptDialogOpen}
				onOpenChange={open => {
					setIsTranscriptDialogOpen(open);
					if (!open) {
						// Reset form state when dialog closes
						setShowMeetingNoteForm(false);
						setAccountIdToFetch(null);
						// Reset web team confirmation state
						setShowWebTeamConfirmation(false);
						setWebTeamSummary(null);
						setIsGeneratingWebTeamSummary(false);
					}
				}}
			>
				<DialogPopup styles={[showMeetingNoteForm ? styleSheet.transcriptDialogWide : styleSheet.transcriptDialog]}>
					<DialogHeader>
						<DialogTitle>
							{showWebTeamConfirmation || showContentTeamConfirmation
								? 'Generate Additional Notes'
								: showMeetingNoteForm
									? 'Record Meeting Note'
									: `Transcript Matches: ${selectedMeeting?.summary || 'Meeting'}`}
						</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.transcriptDialogBody]}>
						{transcriptsMutation.isPending && (
							<div className={css(styleSheet.loadingContainer)}>
								<LoadingSpinner type='large' />
								<span className={css(styleSheet.loadingText)}>Searching for matching Zoom transcripts...</span>
							</div>
						)}

						{!transcriptsMutation.isPending && selectedMeetingWithTranscripts && !showWebTeamConfirmation && (
							<>
								{/* Show warning banner for partial failures when we have results */}
								{selectedMeetingWithTranscripts.hasPartialFailures &&
									selectedMeetingWithTranscripts.transcriptMatches &&
									selectedMeetingWithTranscripts.transcriptMatches.length > 0 && (
										<div className={css(styleSheet.warningBanner)}>
											<span className={css(styleSheet.warningIcon)}>&#9888;</span>
											<span>
												{selectedMeetingWithTranscripts.transcriptLoadingStatus ||
													`${selectedMeetingWithTranscripts.failedTranscriptDownloads} transcript(s) could not be loaded. Some meetings may be missing from results.`}
											</span>
										</div>
									)}

								{/* Show API error if present */}
								{selectedMeetingWithTranscripts.matchError && (
									<div className={css(styleSheet.errorBanner)}>
										<span className={css(styleSheet.errorIcon)}>&#10060;</span>
										<span className={css(styleSheet.errorText)}>{selectedMeetingWithTranscripts.matchError}</span>
										<button
											className={css(styleSheet.retryButton)}
											onClick={() => selectedMeeting && handleFindTranscript(selectedMeeting)}
										>
											Retry
										</button>
									</div>
								)}

								{/* Show all-failed error when no results but failures occurred */}
								{!selectedMeetingWithTranscripts.matchError &&
									(!selectedMeetingWithTranscripts.transcriptMatches ||
										selectedMeetingWithTranscripts.transcriptMatches.length === 0) &&
									selectedMeetingWithTranscripts.failedTranscriptDownloads &&
									selectedMeetingWithTranscripts.failedTranscriptDownloads > 0 && (
										<div className={css(styleSheet.errorBanner)}>
											<span className={css(styleSheet.errorIcon)}>&#10060;</span>
											<span className={css(styleSheet.errorText)}>
												Unable to load Zoom transcripts. Please try again.
											</span>
											<button
												className={css(styleSheet.retryButton)}
												onClick={() => selectedMeeting && handleFindTranscript(selectedMeeting)}
											>
												Retry
											</button>
										</div>
									)}

								{selectedMeetingWithTranscripts.transcriptMatches &&
								selectedMeetingWithTranscripts.transcriptMatches.length > 0 ? (
									<div>
										{showMeetingNoteForm && selectedTranscript ? (
											// When form is shown, render it without the card wrapper
											<>
												<div className={css(styleSheet.meetingNoteForm)}>
													{/* Validation Errors */}
													{validationErrors.length > 0 && (
														<div className={css(styleSheet.validationErrors)}>
															{validationErrors.map((error, idx) => (
																<div key={idx} className={css(styleSheet.validationError)}>
																	{error}
																</div>
															))}
														</div>
													)}

													{/* Two Column Layout */}
													<div className={css(styleSheet.formColumns)}>
														{/* Left Column */}
														<div className={css(styleSheet.formLeftColumn)}>
															{/* Account Selection/Display */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.formLabel)}>
																	Account <span className={css(styleSheet.required)}>*</span>
																</label>
																{selectedAccount ? (
																	<div className={css(styleSheet.selectedAccountDisplay)}>
																		<span>{selectedAccount.companyName}</span>
																		<button
																			className={css(styleSheet.changeAccountButton)}
																			onClick={() => {
																				setSelectedAccount(null);
																				setAccountSearchValue('');
																			}}
																		>
																			Change
																		</button>
																	</div>
																) : (
																	<Combobox
																		items={accounts}
																		value={selectedAccount}
																		onValueChange={setSelectedAccount}
																		inputValue={accountSearchValue}
																		onInputValueChange={handleAccountSearchValueChange}
																		itemToStringLabel={(account: IAccount) => account.companyName || ''}
																		isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
																	>
																		<ComboboxControl>
																			<ComboboxValue>
																				<ComboboxInput placeholder='Search for account...' />
																			</ComboboxValue>
																		</ComboboxControl>
																		<ComboboxContent>
																			<ComboboxStatus>
																				{accountsQuery.isLoading
																					? 'Searching...'
																					: accounts.length === 0 && accountSearchValue
																						? `No accounts found for "${accountSearchValue}"`
																						: accounts.length > 0
																							? `${accounts.length} account${accounts.length === 1 ? '' : 's'} found`
																							: 'Start typing to search accounts...'}
																			</ComboboxStatus>
																			<ComboboxList>
																				{(account: IAccount) => (
																					<ComboboxAvatarItem
																						key={account.id}
																						value={account}
																						avatarImageUrl={''}
																						avatarFallbackText={account.companyName?.substring(0, 2).toUpperCase()}
																						title={account.companyName}
																					/>
																				)}
																			</ComboboxList>
																		</ComboboxContent>
																	</Combobox>
																)}
															</div>

															{/* Meeting Note Content */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.formLabel)}>
																	Meeting Notes <span className={css(styleSheet.required)}>*</span>
																</label>
																<textarea
																	className={css(styleSheet.textarea)}
																	value={meetingNoteContent}
																	onChange={e => setMeetingNoteContent(e.target.value)}
																	rows={15}
																	placeholder='Enter meeting notes...'
																/>
															</div>
														</div>

														{/* Right Column */}
														<div className={css(styleSheet.formRightColumn)}>
															{/* Meeting Type */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.formLabel)}>
																	Meeting Type <span className={css(styleSheet.required)}>*</span>
																</label>
																<Combobox
																	items={Object.values(CsmMeetingTypes)}
																	value={meetingType}
																	onValueChange={value => setMeetingType(value as CsmMeetingTypes)}
																>
																	<ComboboxControl>
																		<ComboboxValue>
																			<ComboboxInput placeholder='Search meeting type...' />
																		</ComboboxValue>
																		<ComboboxClear />
																		<ComboboxIcon />
																	</ComboboxControl>
																	<ComboboxContent>
																		<ComboboxEmpty>No meeting types found.</ComboboxEmpty>
																		<ComboboxList>
																			{(type: string) => (
																				<ComboboxItem key={type} value={type}>
																					<ComboboxItemRadioIndicator />
																					{type}
																				</ComboboxItem>
																			)}
																		</ComboboxList>
																	</ComboboxContent>
																</Combobox>
															</div>

															{/* Onboarding Checkbox */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.checkboxLabel)}>
																	<input
																		type='checkbox'
																		checked={isOnboarding}
																		onChange={e => setIsOnboarding(e.target.checked)}
																	/>
																	<span className={css(styleSheet.onboardingText)}>
																		This was part of the onboarding process
																	</span>
																</label>
															</div>

															{/* Meeting Status */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.formLabel)}>Meeting Status</label>
																<Select value={meetingStatus} onValueChange={setMeetingStatus}>
																	<SelectTrigger>
																		<SelectValue />
																	</SelectTrigger>
																	<SelectContent>
																		<SelectItem value='Completed'>Completed</SelectItem>
																		<SelectItem value='No showed'>No showed</SelectItem>
																		<SelectItem value='Rescheduled'>Rescheduled</SelectItem>
																	</SelectContent>
																</Select>
															</div>

															{/* Duration */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.formLabel)}>Duration (minutes)</label>
																<Select value={duration} onValueChange={setDuration}>
																	<SelectTrigger>
																		<SelectValue />
																	</SelectTrigger>
																	<SelectContent>
																		{[15, 30, 45, 60, 90, 120].map(mins => (
																			<SelectItem key={mins} value={mins}>
																				{mins} minutes
																			</SelectItem>
																		))}
																	</SelectContent>
																</Select>
															</div>

															{/* Next Meeting Date */}
															<div className={css(styleSheet.formField)}>
																<label className={css(styleSheet.formLabel)}>Next Meeting Date</label>
																<div className={css(styleSheet.nextMeetingContainer)}>
																	<button
																		type='button'
																		className={css(styleSheet.nextMeetingButton)}
																		onClick={() => setShowNextMeetingDatePicker(!showNextMeetingDatePicker)}
																	>
																		{nextMeetingDate
																			? nextMeetingDate.toLocaleDateString('en-US', {
																					weekday: 'short',
																					year: 'numeric',
																					month: 'short',
																					day: 'numeric',
																				})
																			: 'Select date...'}
																	</button>
																	{nextMeetingDate && (
																		<button
																			type='button'
																			className={css(styleSheet.clearDateButton)}
																			onClick={() => setNextMeetingDate(null)}
																		>
																			Clear
																		</button>
																	)}
																</div>
																{showNextMeetingDatePicker && (
																	<div className={css(styleSheet.datePickerContainer)}>
																		<DayPicker
																			allowPastDates={false}
																			onDayClick={day => {
																				setNextMeetingDate(day);
																				setShowNextMeetingDatePicker(false);
																			}}
																			selectedDays={nextMeetingDate || undefined}
																		/>
																	</div>
																)}
															</div>

															{/* Action Buttons */}
															<div className={css(styleSheet.formActions)}>
																<button
																	className={css(styleSheet.cancelButton)}
																	onClick={() => {
																		setShowMeetingNoteForm(false);
																		setValidationErrors([]);
																	}}
																	disabled={isSavingNote}
																>
																	Cancel
																</button>
																<button
																	className={css(styleSheet.saveButton)}
																	onClick={handleSaveMeetingNote}
																	disabled={isSavingNote}
																>
																	{isSavingNote ? 'Saving...' : 'Save Meeting Note'}
																</button>
															</div>
														</div>
													</div>
												</div>
											</>
										) : (
											// When form is not shown, render transcript cards normally
											selectedMeetingWithTranscripts.transcriptMatches.map((match, index) => {
												const isExpanded = expandedTranscripts.has(index);
												const isGeneratingSummary = summarizeMutation.isPending && selectedTranscript === match;
												return (
													<div key={index} className={css(styleSheet.transcriptCard)}>
														<div className={css(styleSheet.transcriptCardHeader)}>
															<div className={css(styleSheet.fullWidth)}>
																<div className={css(styleSheet.transcriptTopic)}>
																	{match.meetingTopic || 'Untitled Meeting'}
																</div>
																<div className={css(styleSheet.transcriptMeta)}>
																	<strong>Duration:</strong> {match.durationMinutes} minutes
																	<span className={css(styleSheet.transcriptDivider)}>•</span>
																	<strong>Match Score:</strong>{' '}
																	<span className={css(styleSheet.matchScore, getMatchScoreStyle(match.matchScore))}>
																		{Math.round(match.matchScore)}%
																	</span>
																</div>
																{match.matchReasons && match.matchReasons.length > 0 && (
																	<div className={css(styleSheet.transcriptReasons)}>
																		{match.matchReasons.join(', ')}
																	</div>
																)}
															</div>
															<button
																className={css(styleSheet.transcriptLink)}
																onClick={() => handleRecordMeetingNote(match)}
																disabled={isGeneratingSummary}
															>
																{isGeneratingSummary ? 'Preparing Note...' : 'Record Meeting Note'}
															</button>
														</div>

														{isExpanded && match.transcriptContent && (
															<div className={css(styleSheet.expandedContent)}>
																<div className={css(styleSheet.transcriptContent)}>{match.transcriptContent}</div>
															</div>
														)}

														{match.transcriptContent && (
															<div className={css(styleSheet.transcriptCardFooter)}>
																<button
																	className={css(styleSheet.viewTranscriptLink)}
																	onClick={() => toggleTranscriptExpansion(index)}
																>
																	{isExpanded ? 'Hide Transcript' : 'View Full Transcript'}
																</button>
															</div>
														)}
													</div>
												);
											})
										)}
									</div>
								) : (
									// Only show "no transcripts" message if there are no errors
									!selectedMeetingWithTranscripts.matchError &&
									!selectedMeetingWithTranscripts.failedTranscriptDownloads && (
										<div className={css(styleSheet.noDataMessage)}>
											No matching Zoom transcripts found for this meeting.
										</div>
									)
								)}
							</>
						)}

						{/* Team Summary Confirmation Screens */}
						{(showWebTeamConfirmation || showContentTeamConfirmation) && (
							<div className={css(styleSheet.confirmationContainer)}>
								{/* Success Message Header - shown once above both columns */}
								{!webTeamSummary && !contentTeamSummary && (
									<div className={css(styleSheet.confirmationMessage)}>
										<div className={css(styleSheet.confirmationTitle)}>Generate Additional Notes</div>
										<div className={css(styleSheet.confirmationText)}>
											{showWebTeamConfirmation && showContentTeamConfirmation
												? 'This account has website services and custom content calendar enabled. Generate intake summaries below:'
												: showWebTeamConfirmation
													? 'This account has website services enabled. Would you like to generate a web team intake summary?'
													: 'This account has custom content calendar enabled. Would you like to generate a content team intake summary?'}
										</div>
									</div>
								)}

								{/* Two-column layout for panels */}
								<div className={css(styleSheet.confirmationColumns)}>
									{/* Web Team Panel */}
									{showWebTeamConfirmation && (
										<div
											className={css(
												styleSheet.confirmationColumn,
												!showContentTeamConfirmation && styleSheet.confirmationColumnFull
											)}
										>
											{!webTeamSummary ? (
												// Confirmation prompt with editable Deep Research fields
												<div className={css(styleSheet.confirmationPanel)}>
													<h3 className={css(styleSheet.confirmationPanelTitle)}>Web Team Intake</h3>

													{/* Editable Deep Research fields */}
													<div className={css(styleSheet.deepResearchFields)}>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Business Type</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={webTeamBusinessType}
																onChange={e => setWebTeamBusinessType(e.target.value)}
																placeholder='e.g., Insurance Agency'
																disabled={isGeneratingWebTeamSummary}
															/>
														</div>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Location</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={webTeamLocation}
																onChange={e => setWebTeamLocation(e.target.value)}
																placeholder='e.g., Raleigh, NC'
																disabled={isGeneratingWebTeamSummary}
															/>
														</div>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={webTeamWebsiteUrl}
																onChange={e => setWebTeamWebsiteUrl(e.target.value)}
																placeholder='e.g., https://example.com'
																disabled={isGeneratingWebTeamSummary}
															/>
														</div>
													</div>

													<div className={css(styleSheet.confirmationActions)}>
														<Button
															variant='secondary'
															onClick={handleSkipWebTeamSummary}
															disabled={isGeneratingWebTeamSummary}
														>
															Skip
														</Button>
														<Button
															variant='success'
															onClick={handleGenerateWebTeamSummary}
															isLoading={isGeneratingWebTeamSummary}
															disabled={isGeneratingWebTeamSummary}
														>
															Generate Web Team Summary
														</Button>
													</div>
												</div>
											) : (
												// Summary display
												<>
													<div className={css(styleSheet.summaryHeader)}>
														<h3>Web Team Intake Summary</h3>
														<Button variant='primary' onClick={handleCopyWebTeamSummary}>
															Copy
														</Button>
													</div>
													<div className={css(styleSheet.summaryNote)}>
														{
															"We'll automatically save this note to the account along with a research report for the web team."
														}
													</div>
													<div className={css(styleSheet.summaryContent)}>{webTeamSummary}</div>
												</>
											)}
										</div>
									)}

									{/* Content Team Panel */}
									{showContentTeamConfirmation && (
										<div
											className={css(
												styleSheet.confirmationColumn,
												!showWebTeamConfirmation && styleSheet.confirmationColumnFull
											)}
										>
											{!contentTeamSummary ? (
												// Confirmation prompt
												<div className={css(styleSheet.confirmationPanel)}>
													<h3 className={css(styleSheet.confirmationPanelTitle)}>Content Team Intake</h3>
													<div className={css(styleSheet.confirmationActions)}>
														<Button
															variant='secondary'
															onClick={handleSkipContentTeamSummary}
															disabled={isGeneratingContentTeamSummary}
														>
															Skip
														</Button>
														<Button
															variant='success'
															onClick={handleGenerateContentTeamSummary}
															isLoading={isGeneratingContentTeamSummary}
															disabled={isGeneratingContentTeamSummary}
														>
															Generate Content Team Summary
														</Button>
													</div>
												</div>
											) : (
												// Summary display
												<>
													<div className={css(styleSheet.summaryHeader)}>
														<h3>Content Team Intake Summary</h3>
														<Button variant='primary' onClick={handleCopyContentTeamSummary}>
															Copy
														</Button>
													</div>
													<div className={css(styleSheet.summaryNote)}>
														{"We'll automatically save this note to the account and send it to ClickUp."}
													</div>
													<div className={css(styleSheet.summaryContent)}>{contentTeamSummary}</div>
												</>
											)}
										</div>
									)}
								</div>

								{/* Close button - shown when at least one summary is generated */}
								{(webTeamSummary || contentTeamSummary) && (
									<div className={css(styleSheet.summaryActions)}>
										<Button variant='primary' onClick={handleCloseConfirmationScreen}>
											Close
										</Button>
									</div>
								)}
							</div>
						)}
					</DialogBody>
				</DialogPopup>
			</Dialog>

			{/* No Show Dialog */}
			<Dialog
				open={isNoShowDialogOpen}
				onOpenChange={open => {
					setIsNoShowDialogOpen(open);
					if (!open) {
						// Reset state when dialog closes
						setNoShowMeeting(null);
						setNoShowAccount(null);
						setNoShowAccountSearchValue('');
					}
				}}
			>
				<DialogPopup styles={[styleSheet.noShowDialog]}>
					<DialogHeader>
						<DialogTitle>Mark No Show</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.noShowDialogBody]}>
						{noShowMeeting && (
							<>
								<div className={css(styleSheet.transcriptPreview)}>
									<div className={css(styleSheet.transcriptPreviewTitle)}>
										{noShowMeeting.summary || 'Untitled Meeting'}
									</div>
									<div className={css(styleSheet.transcriptPreviewContent)}>
										{formatTime(noShowMeeting.startTime)} • {noShowMeeting.durationMinutes} min
									</div>
								</div>

								{/* Account Selection */}
								<div className={css(styleSheet.noShowFormField)}>
									<label className={css(styleSheet.noShowFormLabel)}>
										Account <span className={css(styleSheet.required)}>*</span>
									</label>
									{noShowAccount ? (
										<div className={css(styleSheet.selectedAccountDisplay)}>
											<span>{noShowAccount.companyName}</span>
											<button
												className={css(styleSheet.changeAccountButton)}
												onClick={() => {
													setNoShowAccount(null);
													setNoShowAccountSearchValue('');
												}}
											>
												Change
											</button>
										</div>
									) : (
										<Combobox
											items={accounts}
											value={noShowAccount}
											onValueChange={setNoShowAccount}
											inputValue={noShowAccountSearchValue}
											onInputValueChange={handleNoShowAccountSearchValueChange}
											itemToStringLabel={(account: IAccount) => account.companyName || ''}
											isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
										>
											<ComboboxControl>
												<ComboboxValue>
													<ComboboxInput placeholder='Search for account...' />
												</ComboboxValue>
											</ComboboxControl>
											<ComboboxContent>
												<ComboboxStatus>
													{accountsQuery.isLoading
														? 'Searching...'
														: accounts.length === 0 && noShowAccountSearchValue
															? `No accounts found for "${noShowAccountSearchValue}"`
															: accounts.length > 0
																? `${accounts.length} account${accounts.length === 1 ? '' : 's'} found`
																: 'Start typing to search accounts...'}
												</ComboboxStatus>
												<ComboboxList>
													{(account: IAccount) => (
														<ComboboxAvatarItem
															key={account.id}
															value={account}
															avatarImageUrl={''}
															avatarFallbackText={account.companyName?.substring(0, 2).toUpperCase()}
															title={account.companyName}
														/>
													)}
												</ComboboxList>
											</ComboboxContent>
										</Combobox>
									)}
								</div>

								{/* Dialog Actions */}
								<div className={css(styleSheet.noShowDialogActions)}>
									<button
										className={css(styleSheet.cancelButton)}
										onClick={() => setIsNoShowDialogOpen(false)}
										disabled={isSavingNoShow}
									>
										Cancel
									</button>
									<button className={css(styleSheet.saveButton)} onClick={handleSaveNoShow} disabled={isSavingNoShow}>
										{isSavingNoShow ? 'Saving...' : 'Record No Show'}
									</button>
								</div>
							</>
						)}
					</DialogBody>
				</DialogPopup>
			</Dialog>
		</div>
	);
}
